import type { AttemptStatus, FailureType, Harness } from '@nightshift/ui-types';

/**
 * Runs ledger URL state.
 *
 * Ticket, state, status and harness are shareable. `outcome=failed` is kept as a
 * deep-link alias of status=Failed so existing capture URLs still work.
 */
export interface RunsFilter {
  ticket: string | null;
  state: string | null;
  status: string | null;
  harness: string | null;
  outcome: string | null;
}

export const RUN_STATUSES: readonly AttemptStatus[] = [
  'Queued',
  'Starting',
  'Running',
  'Succeeded',
  'Failed',
  'Cancelled',
  'Interrupted',
  'Superseded',
];

export const RUN_HARNESSES: readonly { id: Harness; label: string }[] = [
  { id: 'claude', label: 'Claude Code' },
  { id: 'codex', label: 'Codex' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'kimi', label: 'Kimi' },
];

export function runsFilterFrom(input: {
  ticket?: string | null;
  state?: string | null;
  status?: string | null;
  harness?: string | null;
  outcome?: string | null;
}): RunsFilter {
  return {
    ticket: emptyToNull(input.ticket),
    state: emptyToNull(input.state),
    status: emptyToNull(input.status),
    harness: emptyToNull(input.harness),
    outcome: emptyToNull(input.outcome),
  };
}

export function runsFilterActive(filter: RunsFilter): boolean {
  return (
    filter.ticket !== null ||
    filter.state !== null ||
    filter.status !== null ||
    filter.harness !== null ||
    filter.outcome !== null
  );
}

export function runsEmptyCopy(opts: {
  filterActive: boolean;
  projectSlug: string | null;
}): { title: string; body: string } {
  if (opts.filterActive) {
    return {
      title: 'Nothing matches these filters.',
      body: 'Clear the filters to see every attempt this project has recorded.',
    };
  }
  if (opts.projectSlug) {
    return {
      title: 'No attempts yet.',
      body: 'This project has no execution attempts. Rows appear here when a runner reports one.',
    };
  }
  return {
    title: 'No attempts yet.',
    body: 'Pick a project, or wait for a runner to report an attempt. Every invocation lands here.',
  };
}

/** RateLimit is information, not a red failure. */
export function failureTypeStyle(kind: FailureType | null): { color: string; label: string } | null {
  if (kind === null) return null;
  if (kind === 'RateLimit') {
    return { color: 'var(--info-text)', label: 'rate limit' };
  }
  return { color: 'var(--danger-text)', label: kind };
}

export function statusStyle(status: AttemptStatus, failureType: FailureType | null): {
  color: string;
  label: string;
} {
  if (status === 'Running' || status === 'Starting' || status === 'Queued') {
    return { color: 'var(--good-text)', label: status.toLowerCase() };
  }
  if (status === 'Succeeded') {
    return { color: 'var(--good-text)', label: 'succeeded' };
  }
  if (status === 'Failed') {
    const failure = failureTypeStyle(failureType);
    return failure ?? { color: 'var(--danger-text)', label: 'failed' };
  }
  return { color: 'var(--text-3)', label: status.toLowerCase() };
}

function emptyToNull(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
